import { absolutizeServiceRoot } from "./absolutizeServiceRoot";
import { BASE_WELL_GRAY } from "./baseWellGlyphs";

export interface BaseWellTileUrlOptions {
  // The base layer's own filter id (see useBaseLayerFilterSync). Required by
  // eva-map-svc's basewelltile endpoints — a request without it 400s.
  filterId?: string;
  // A short, stable fingerprint of the org's ordered SURVEY-source preference
  // (see buildSurveyPreferenceToken). The server ignores its value and
  // resolves the preference from the JWT — this exists purely so a preference
  // change produces a different URL, which is what makes Mapbox GL refetch:
  // it never re-requests a tile it already holds under the same URL.
  prefHash?: string;
  // Same idea, for the org's ordered HEADER-source preference (see
  // buildHeaderPreferenceToken) — a distinct, separately-settable preference
  // that decides which source's Header.ResourcePlay wins, which in turn can
  // change the base layer's WELL SET (not just its pixels). Used by both the
  // MVT and raster endpoints — see getBaseWellRasterTileUrl.
  attrPrefHash?: string;
  // Resolved `#rrggbb`. Raster only: the server paints the sub-z8 tile in this
  // colour, so it must be part of the URL. The MVT layers colour client-side.
  color?: string;
}

/**
 * Builds the `?f=...&pref=...&attrPref=...` query string shared by every
 * base-well tile endpoint (MVT and raster). Kept in one place so the URL
 * builders below can never drift on how `f`/`pref`/`attrPref` are assembled.
 *
 * Both endpoints include `attrPref` (EVA-35 PR #6601 round-4 review): the
 * raster is RENDERED FROM the same filter well-set the MVT tile reads, and
 * that set is keyed by header preference server-side (eva-map-svc's
 * FilterWellSetCache) precisely because which source's Header.ResourcePlay
 * wins for a well depends on that order — a one-colour raster reads no
 * attributes itself, but the WELL SET it paints does vary. Two callers on the
 * same filterId with different header preferences must not resolve to the
 * same client URL and share a cached PNG rendered from the wrong one.
 */
function buildBaseWellTileQuery(options: BaseWellTileUrlOptions): string | undefined {
  const { filterId, prefHash, attrPrefHash } = options;
  if (!filterId) return undefined;

  const params = [`f=${encodeURIComponent(filterId)}`];
  if (prefHash) {
    params.push(`pref=${encodeURIComponent(prefHash)}`);
  }
  if (attrPrefHash) {
    params.push(`attrPref=${encodeURIComponent(attrPrefHash)}`);
  }
  return params.join("&");
}

// Mapbox GL fetches vector AND raster tiles from a web worker that silently
// drops relative tile URLs (the layer renders nothing, with no error).
// Deployed envs set REACT_APP_MAP_SERVICE absolute, but dev setups that proxy
// the service under a relative path must be absolutized against the page
// origin. shouldAttachMapAuth absolutizes with the same helper so the Bearer
// token still gets attached to the URLs these produce.
function absolutizedMapServiceRoot(): string {
  return absolutizeServiceRoot(process.env.REACT_APP_MAP_SERVICE);
}

/**
 * Generates the MVT tile URL for the base well layer.
 *
 * The base well layer now posts its OWN filter (EVA-35), independent of the
 * well-list filter, and the tile endpoint reads that filter server-side by id
 * — `f` is REQUIRED (eva-map-svc answers 400 without it). There is no more
 * client-built `?plays=` param: the resource-play selection is baked into the
 * filter behind `filterId` instead. Until a filterId exists (the very first
 * post hasn't resolved yet), this returns undefined so callers know not to
 * create the source/layers yet.
 * @param options Configuration options for the base well layer
 * @returns The formatted MVT tile URL template, or undefined with no filterId
 */
export function getBaseWellLayerTileUrl(
  options: BaseWellTileUrlOptions
): string | undefined {
  const query = buildBaseWellTileQuery(options);
  if (!query) return undefined;

  return `${absolutizedMapServiceRoot()}/basewelltile/{z}/{x}/{y}?${query}`;
}

/**
 * Generates the raster tile URL for the base well layer — the same
 * single-colour-dot renderer as the interactive well layer's low-zoom raster,
 * drawn below HZ_RASTER_MVT_CROSSOVER_ZOOM (see useMapTileSourceManager for
 * the interactive layer's equivalent). Same `f`/`pref`/`attrPref` contract as
 * the MVT endpoint above, so the two are built from one shared query-string
 * builder and can never drift on that part of the URL.
 *
 * INCLUDES `attrPrefHash` (EVA-35 PR #6601 round-4 review — round 2/3
 * previously excluded it here on the reasoning that "a one-colour raster
 * reads no attributes", which is true of the PIXELS but not of the WELL SET:
 * the raster is rendered from the same filter well-set the MVT tile reads,
 * and eva-map-svc's FilterWellSetCache keys that set by header preference
 * because Header.ResourcePlay resolution depends on it. Omitting it here let
 * the client resolve one preference's raster URL to the exact same URL (and
 * therefore the exact same cached tile) as another preference's, which
 * eva-map-svc's own cache key omission compounds server-side — see that
 * repo's BaseWellTileController.BuildRasterCacheKey.
 * @param options Configuration options for the base well layer
 * @returns The formatted raster tile URL template, or undefined with no filterId
 */
export function getBaseWellRasterTileUrl(
  options: BaseWellTileUrlOptions
): string | undefined {
  const query = buildBaseWellTileQuery(options);
  if (!query) return undefined;

  // Omitted at the default so the URL stays byte-identical and warm server cache entries still hit.
  const color = options.color?.toLowerCase();
  const colorParam =
    color && color !== BASE_WELL_GRAY && /^#[0-9a-f]{6}$/.test(color)
      ? `&c=${color.slice(1)}`
      : "";

  return `${absolutizedMapServiceRoot()}/basewelltile/raster/{z}/{x}/{y}?${query}${colorParam}`;
}

/**
 * Generates the tile URL for the legacy GeoServer WMS base well layer
 * (pre-EVA-35, used when the "Base Layer Refactor" flag is off).
 * @param options Configuration options for the base well layer
 * @returns The formatted WMS tile URL
 */
export function getLegacyBaseWellLayerTileUrl(options: {
  hasGdc: boolean;
  hasIhs: boolean;
  orgId: string;
  baseLayerResourcePlays?: readonly string[];
  hasFeature?: (feature: string) => boolean;
}): string {
  const { hasGdc, hasIhs, orgId, baseLayerResourcePlays, hasFeature } = options;

  const geoserver_root = process.env.REACT_APP_GEOSERVER;
  if (!geoserver_root) {
    // eslint-disable-next-line no-console
    console.warn(
      "REACT_APP_GEOSERVER is unset — legacy base well layer tiles will be blank."
    );
  }
  const forceIhs = hasFeature ? hasFeature("Force IHS Base Layer") : false;

  const layerName =
    forceIhs || (!hasGdc && hasIhs)
      ? `cite:wells_ihs_${orgId.toLowerCase()}`
      : "cite:wells";

  const viewParams =
    baseLayerResourcePlays && baseLayerResourcePlays.length > 0
      ? `&viewparams=plays:${encodeURIComponent(baseLayerResourcePlays.join("|"))}`
      : "";

  return `${geoserver_root}/geoserver/cite/wms?bbox={bbox-epsg-3857}&STYLE=well&format=image/png&service=WMS&version=1.1.1&request=GetMap&srs=EPSG:3857&transparent=true&width=256&height=256&layers=${layerName}${viewParams}`;
}
